import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  BarChart3, Store, FolderTree, Tag, MapPin, Phone, MessageSquare,
  Mail, Globe, Loader2, Search, Share2, UserPlus,
} from 'lucide-react';

export const AdminAnalyticsTab = ({
  analyticsSummary,
  refreshAnalytics,
  handleClearAnalytics,
  linkClickAnalytics,
  showAllDirections, setShowAllDirections,
}) => {
  const [searchKeywords, setSearchKeywords] = useState([]);
  const [loadingKeywords, setLoadingKeywords] = useState(false);
  const API_URL = window.location.origin;

  const fetchSearchKeywords = async () => {
    setLoadingKeywords(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/analytics/search-keywords?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSearchKeywords(data.keywords || []);
      }
    } catch (e) { /* ignore */ }
    setLoadingKeywords(false);
  };

  useEffect(() => {
    fetchSearchKeywords();
  }, []);

  if (!analyticsSummary) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
        <p>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-[#252525] dark:text-white flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[#FFD12D]" />
          Firebase Analytics Dashboard
        </h3>
        <div className="flex gap-2">
          <Button onClick={refreshAnalytics} variant="outline" className="border-[#FFD12D] text-[#252525] hover:bg-[#FFD12D] dark:text-white dark:hover:text-[#252525]">Refresh</Button>
          <Button onClick={handleClearAnalytics} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">Clear Data</Button>
        </div>
      </div>

      {/* Summary Cards - Today */}
      <div>
        <h4 className="font-semibold text-[#252525] dark:text-white mb-3">Today&apos;s Activity</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { value: analyticsSummary.today.appOpened, label: 'App Opened', color: 'blue' },
            { value: analyticsSummary.today.screenViews, label: 'Screen Views', color: 'purple' },
            { value: analyticsSummary.today.loginSuccess, label: 'Login Success', color: 'green' },
            { value: analyticsSummary.today.loginFailure, label: 'Login Failures', color: 'red' },
            { value: analyticsSummary.today.stallViewed, label: 'Stalls Viewed', color: 'yellow' },
            { value: analyticsSummary.today.stallCreated, label: 'Stalls Created', color: 'teal' },
            { value: analyticsSummary.today.errors, label: 'Errors', color: 'orange' },
            { value: analyticsSummary.today.backendActivity, label: 'Backend Activity', color: 'indigo' },
          ].map(({ value, label, color }) => (
            <div key={label} className={`bg-${color}-50 dark:bg-${color}-900/20 p-4 rounded-lg border border-${color}-200 dark:border-${color}-800`}>
              <div className={`text-2xl font-bold text-${color}-600`}>{value}</div>
              <div className={`text-sm text-${color}-600/80`}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Cards - Last 7 Days */}
      <div>
        <h4 className="font-semibold text-[#252525] dark:text-white mb-3">Last 7 Days</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { value: analyticsSummary.last7Days.total, label: 'Total Events' },
            { value: analyticsSummary.last7Days.appOpened, label: 'App Opened' },
            { value: analyticsSummary.last7Days.stallViewed, label: 'Stalls Viewed' },
            { value: analyticsSummary.last7Days.loginSuccess, label: 'Logins' },
          ].map(({ value, label }) => (
            <div key={label} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="text-2xl font-bold text-[#252525] dark:text-white">{value}</div>
              <div className="text-sm text-gray-500">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div>
        <h4 className="font-semibold text-[#252525] dark:text-white mb-3">Recent Activity</h4>
        <div className="bg-gray-50 dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 max-h-80 overflow-y-auto">
          {analyticsSummary.recentEvents.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No events recorded yet</p>
              <p className="text-sm">Events will appear here as users interact with the app</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {analyticsSummary.recentEvents.slice(0, 30).map((event) => (
                <div key={event.id} className="p-3 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-semibold mr-2 ${
                        event.name.includes('error') ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                        event.name.includes('login') ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                        event.name.includes('stall') ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                        event.name.includes('screen') || event.name.includes('page') ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                        'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      }`}>
                        {event.name.replace(/_/g, ' ')}
                      </span>
                      {event.params && Object.keys(event.params).length > 0 && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {Object.entries(event.params).filter(([k]) => !k.includes('timestamp')).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">{event.time}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top Search Keywords */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-[#252525] dark:text-white flex items-center gap-2">
            <Search className="w-4 h-4 text-[#FFD12D]" />
            Top Search Keywords (Last 30 Days)
          </h4>
          <Button onClick={fetchSearchKeywords} variant="ghost" size="sm" disabled={loadingKeywords} className="text-xs">
            {loadingKeywords ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Refresh'}
          </Button>
        </div>
        {searchKeywords.length === 0 ? (
          <div className="bg-gray-50 dark:bg-[#252525] rounded-lg p-4 text-center text-sm text-gray-500 dark:text-gray-400">
            {loadingKeywords ? 'Loading...' : 'No search data yet. Keywords will appear as users search.'}
          </div>
        ) : (
          <div className="bg-white dark:bg-[#1a1a1a] rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm" data-testid="search-keywords-table">
              <thead>
                <tr className="bg-gray-50 dark:bg-[#252525] border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 px-4 font-semibold text-gray-600 dark:text-gray-400">#</th>
                  <th className="text-left py-2 px-4 font-semibold text-gray-600 dark:text-gray-400">Keyword</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-600 dark:text-gray-400">Searches</th>
                  <th className="text-right py-2 px-4 font-semibold text-gray-600 dark:text-gray-400">Avg Results</th>
                </tr>
              </thead>
              <tbody>
                {searchKeywords.map((kw, i) => (
                  <tr key={kw.term} className="border-b border-gray-100 dark:border-gray-800 last:border-0 hover:bg-[#FFD12D]/5">
                    <td className="py-2 px-4 text-gray-400">{i + 1}</td>
                    <td className="py-2 px-4 font-medium text-[#252525] dark:text-white">{kw.term}</td>
                    <td className="py-2 px-4 text-right">
                      <span className="inline-block bg-[#FFD12D]/20 text-[#252525] dark:text-[#FFD12D] px-2 py-0.5 rounded-full text-xs font-semibold">
                        {kw.count}
                      </span>
                    </td>
                    <td className="py-2 px-4 text-right text-gray-500 dark:text-gray-400">{kw.avg_results}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top 10 Rankings */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[
          { title: 'Top 10 Viewed Stalls', icon: Store, data: analyticsSummary.topStalls, suffix: 'views', color: '#FFD12D', emptyMsg: 'No stall views recorded yet' },
          { title: 'Top 10 Categories', icon: FolderTree, data: analyticsSummary.topCategories, suffix: 'views', color: 'rgb(147, 51, 234)', emptyMsg: 'No category data yet' },
          { title: 'Top 10 Tags', icon: Tag, data: analyticsSummary.topTags, suffix: 'views', color: 'rgb(22, 163, 74)', emptyMsg: 'No tag data recorded yet', prefix: '#' },
          { title: 'Top 10 Markets', icon: MapPin, data: analyticsSummary.topMarkets, suffix: 'views', color: 'rgb(37, 99, 235)', emptyMsg: 'No market data recorded yet' },
        ].map(({ title, icon: Icon, data, suffix, color: itemColor, emptyMsg, prefix }) => (
          <div key={title}>
            <h4 className="font-semibold text-[#252525] dark:text-white mb-3 flex items-center gap-2">
              <Icon className="w-4 h-4 text-[#FFD12D]" /> {title}
            </h4>
            <div className="bg-gray-50 dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              {!data?.length ? (
                <div className="p-4 text-center text-gray-500 text-sm">{emptyMsg}</div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {data.map((item, index) => (
                    <div key={item.name} className="flex items-center justify-between p-3 hover:bg-gray-100 dark:hover:bg-gray-800/50">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          index === 0 ? 'bg-[#FFD12D] text-[#252525]' :
                          index === 1 ? 'bg-gray-300 text-gray-700' :
                          index === 2 ? 'bg-orange-300 text-orange-800' :
                          'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                        }`}>{index + 1}</span>
                        <span className="text-sm text-[#252525] dark:text-white truncate max-w-[150px]">{prefix || ''}{item.name}</span>
                      </div>
                      <span className="text-sm font-semibold" style={{ color: itemColor }}>{item.count} {suffix}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Link Click Analytics */}
      {linkClickAnalytics && (
        <div className="space-y-6">
          <h4 className="font-semibold text-[#252525] dark:text-white text-lg">Link Click Analytics</h4>

          {/* Contact Details Clicks */}
          <div>
            <h5 className="font-semibold text-[#252525] dark:text-white mb-3 flex items-center gap-2">
              <Phone className="w-4 h-4 text-[#FFD12D]" /> Contact Details Clicks
            </h5>
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
              <table className="w-full text-sm min-w-[400px]">
                <thead className="bg-gray-50 dark:bg-[#252525]">
                  <tr>
                    <th className="text-left p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Link Type</th>
                    <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Today</th>
                    <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 7 Days</th>
                    <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 30 Days</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {linkClickAnalytics.contactDetails.map((item) => (
                    <tr key={item.type} className="hover:bg-gray-50 dark:hover:bg-[#252525]/50">
                      <td className="p-3 text-[#252525] dark:text-white whitespace-nowrap">
                        <span className="flex items-center gap-2">
                          {item.type === 'phone' && <Phone className="w-4 h-4 text-[#FFD12D]" />}
                          {item.type === 'whatsapp' && <MessageSquare className="w-4 h-4 text-green-500" />}
                          {item.type === 'email' && <Mail className="w-4 h-4 text-blue-500" />}
                          {item.type === 'website' && <Globe className="w-4 h-4 text-purple-500" />}
                          {item.label}
                        </span>
                      </td>
                      <td className="p-3 text-center text-green-600 font-semibold">{item.today}</td>
                      <td className="p-3 text-center text-purple-600 font-semibold">{item.last7Days}</td>
                      <td className="p-3 text-center text-yellow-600 font-semibold">{item.last30Days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Social Media Clicks */}
          <div>
            <h5 className="font-semibold text-[#252525] dark:text-white mb-3 flex items-center gap-2">
              <Globe className="w-4 h-4 text-[#FFD12D]" /> Social Media Clicks
            </h5>
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
              <table className="w-full text-sm min-w-[400px]">
                <thead className="bg-gray-50 dark:bg-[#252525]">
                  <tr>
                    <th className="text-left p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Platform</th>
                    <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Today</th>
                    <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 7 Days</th>
                    <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 30 Days</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {linkClickAnalytics.socialMedia.map((item) => (
                    <tr key={item.type} className="hover:bg-gray-50 dark:hover:bg-[#252525]/50">
                      <td className="p-3 text-[#252525] dark:text-white whitespace-nowrap">{item.label}</td>
                      <td className="p-3 text-center text-green-600 font-semibold">{item.today}</td>
                      <td className="p-3 text-center text-purple-600 font-semibold">{item.last7Days}</td>
                      <td className="p-3 text-center text-yellow-600 font-semibold">{item.last30Days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Engagement Clicks (Share + Save Contact) */}
          {linkClickAnalytics.engagement && (
            <div>
              <h5 className="font-semibold text-[#252525] dark:text-white mb-3 flex items-center gap-2">
                <Share2 className="w-4 h-4 text-[#FFD12D]" /> Engagement Clicks
              </h5>
              <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
                <table className="w-full text-sm min-w-[400px]">
                  <thead className="bg-gray-50 dark:bg-[#252525]">
                    <tr>
                      <th className="text-left p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Action</th>
                      <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Today</th>
                      <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 7 Days</th>
                      <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 30 Days</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {linkClickAnalytics.engagement.map((item) => (
                      <tr key={item.type} className="hover:bg-gray-50 dark:hover:bg-[#252525]/50">
                        <td className="p-3 text-[#252525] dark:text-white whitespace-nowrap">
                          <span className="flex items-center gap-2">
                            {item.type === 'share' && <Share2 className="w-4 h-4 text-[#FFD12D]" />}
                            {item.type === 'save_contact' && <UserPlus className="w-4 h-4 text-[#FFD12D]" />}
                            {item.type === 'share' ? 'Share Stall' : 'Save Contact'}
                          </span>
                        </td>
                        <td className="p-3 text-center text-green-600 font-semibold">{item.today}</td>
                        <td className="p-3 text-center text-purple-600 font-semibold">{item.last7Days}</td>
                        <td className="p-3 text-center text-yellow-600 font-semibold">{item.last30Days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Get Directions Clicks */}
          <div>
            <h5 className="font-semibold text-[#252525] dark:text-white mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#FFD12D]" /> Get Directions Clicks
              {linkClickAnalytics.directionsClicks.length > 10 && (
                <span className="text-xs text-gray-500 font-normal">(showing {showAllDirections ? 'all' : 'top 10'} of {linkClickAnalytics.directionsClicks.length})</span>
              )}
            </h5>
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-700 overflow-x-auto">
              <table className="w-full text-sm min-w-[400px]">
                <thead className="bg-gray-50 dark:bg-[#252525]">
                  <tr>
                    <th className="text-left p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Location</th>
                    <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Today</th>
                    <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 7 Days</th>
                    <th className="text-center p-3 font-semibold text-[#252525] dark:text-white whitespace-nowrap">Last 30 Days</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {linkClickAnalytics.directionsClicks.length === 0 ? (
                    <tr><td colSpan="4" className="p-4 text-center text-gray-500">No directions clicks recorded yet</td></tr>
                  ) : (
                    (showAllDirections ? linkClickAnalytics.directionsClicks : linkClickAnalytics.directionsClicks.slice(0, 10)).map((item, index) => (
                      <tr key={item.market} className="hover:bg-gray-50 dark:hover:bg-[#252525]/50">
                        <td className="p-3 text-[#252525] dark:text-white whitespace-nowrap">
                          <span className="flex items-center gap-2">
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-[#FFD12D] text-[#252525]' : index === 1 ? 'bg-gray-300 text-gray-700' : index === 2 ? 'bg-orange-300 text-orange-800' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            }`}>{index + 1}</span>
                            <MapPin className="w-4 h-4 text-[#FFD12D]" />
                            {item.market}
                          </span>
                        </td>
                        <td className="p-3 text-center text-green-600 font-semibold">{item.today}</td>
                        <td className="p-3 text-center text-purple-600 font-semibold">{item.last7Days}</td>
                        <td className="p-3 text-center text-yellow-600 font-semibold">{item.last30Days}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {linkClickAnalytics.directionsClicks.length > 10 && (
              <button onClick={() => setShowAllDirections(!showAllDirections)} className="mt-2 text-sm text-[#FFD12D] hover:text-[#252525] dark:hover:text-white font-semibold transition-colors">
                {showAllDirections ? '\u2190 Show Top 10 Only' : `View All ${linkClickAnalytics.directionsClicks.length} Locations \u2192`}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Firebase Console Link */}
      <div className="bg-[#FFD12D]/10 border border-[#FFD12D] rounded-lg p-4">
        <p className="text-sm text-[#252525] dark:text-white">
          <strong>Full Analytics:</strong> For detailed reports, user demographics, and advanced analytics, visit the{' '}
          <a href="https://console.firebase.google.com/project/wayo-213b7/analytics" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">
            Firebase Console &rarr;
          </a>
        </p>
      </div>
    </div>
  );
};
